import moment from 'moment-timezone';

const ESTADO_CITA_ACTIVA = 'Activa';
const ESTADO_CITA_ACTIVA_LEGACY = '';
const LABEL_CITA_PASADA = 'Pasada';
const LABEL_CITA_EN_PROCESO_PAGO = 'En proceso de pago';

/**
 * Devuelve la etiqueta de estado de una cita del historial para mostrar en UI.
 *
 * Una cita activa con prepago sin confirmar está pendiente de que el cliente
 * complete el pago, tanto si su fecha es futura como pasada. El campo `pagada`
 * no sirve para esto: se activa al generar la venta, no al pagar el prepago.
 *
 * @param {object} cita             Cita del historial (estado, prepago, confirmada, fecha)
 * @param {object} fechaReferencia  Momento contra el que comparar la fecha (inyectable para tests)
 * @returns {{ label: string, enProcesoPago: boolean }}
 */
export const obtenerEstadoCitaParaUI = (cita, fechaReferencia = moment()) => {
    const esCitaActiva = cita?.estado === ESTADO_CITA_ACTIVA || cita?.estado === ESTADO_CITA_ACTIVA_LEGACY;

    if (!esCitaActiva) {
        return { label: cita?.estado, enProcesoPago: false };
    }

    const prepagoSinConfirmar = cita?.prepago === true && cita?.confirmada === false;
    if (prepagoSinConfirmar) {
        return { label: LABEL_CITA_EN_PROCESO_PAGO, enProcesoPago: true };
    }

    const fechaYaPasada = moment(cita?.fecha).isBefore(fechaReferencia);
    if (fechaYaPasada) {
        return { label: LABEL_CITA_PASADA, enProcesoPago: false };
    }

    return { label: cita?.estado, enProcesoPago: false };
};

/**
 * Id de catálogo del servicio del tramo seleccionado de una cita, o `undefined` si no hay tramo.
 *
 * Con tramo, cancelar solo anula ese servicio (no la cita entera). La consulta de penalización y
 * el borrado tienen que usar el mismo valor para que el importe mostrado sea el que se aplica.
 */
export const servicioIdTramoActual = (cita) => {
    const tieneTramoSeleccionado = Boolean(cita?.servicioActual) && cita?.servicios?.length >= 1;
    if (!tieneTramoSeleccionado) return undefined;

    const refServicio = cita.servicioActual.servicio?._id || cita.servicioActual.servicio;
    return refServicio != null ? String(refServicio) : undefined;
};

export const ESTADO_CANCELACION_NO_PRESENTADO = 'No presentado';

/**
 * Cancelar solo anula un servicio y la cita sigue viva con los demás: hay tramo seleccionado, la
 * cita tiene más de un servicio y no es un no-show (el no-show afecta siempre a la cita completa).
 * Si es el último o único servicio, la cita entera se cancela.
 */
export const anulaServicioDeCitaConVarios = (cita, estadoCita) => {
    const tieneTramo = servicioIdTramoActual(cita) !== undefined;
    const citaSigueConOtrosServicios = cita?.servicios?.length > 1;
    const esNoShow = estadoCita === ESTADO_CANCELACION_NO_PRESENTADO;
    return tieneTramo && citaSigueConOtrosServicios && !esNoShow;
};
